import { useAuth } from "@/context/AuthContext";
import { Link, useNavigate } from "react-router-dom";
import { Store, Package, ShoppingBag, Shield, LogOut, Settings, BadgeCheck, Palette, MessageCircle, Mail } from "lucide-react";
import { Button } from "@/components/ui/button";
import ThemeSwitcher from "@/components/Common/ThemeSwitcher";

const SUPPORT_EMAIL = "forum.ci@outlook.com";

export default function Profile() {
  const { user, shop, logout } = useAuth();
  const nav = useNavigate();

  if (!user) return null;
  const fullName = `${user.first_name || ""} ${user.last_name || ""}`.trim();

  // Build a pre-populated mailto link so support gets full context (user role,
  // shop name, IDs) without the user having to type anything.
  const buildSupportMailto = () => {
    const subject = shop
      ? `Support Forum CI — ${shop.name}`
      : `Support Forum CI — ${fullName || user.email || user.phone || "Utilisateur"}`;
    const lines = [
      "Bonjour l'équipe Forum CI,",
      "",
      "Je vous contacte au sujet de :",
      "[décrivez votre demande ici]",
      "",
      "— Mes informations —",
      `Nom : ${fullName || "—"}`,
      `Email : ${user.email || "—"}`,
      `Téléphone : ${user.phone || "—"}`,
      `Rôle : ${user.role}`,
      `ID utilisateur : ${user.id}`,
    ];
    if (shop) {
      lines.push(
        "",
        "— Ma boutique —",
        `Nom : ${shop.name}`,
        `ID boutique : ${shop.id}`,
        `Ville : ${shop.city || "—"}`,
      );
    }
    lines.push("", "Merci d'avance.");
    return `mailto:${SUPPORT_EMAIL}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(lines.join("\n"))}`;
  };

  const items = [
    shop && { to: "/tableau-bord", label: "Tableau de bord marchand", icon: Store, testid: "profile-dashboard" },
    shop && { to: `/boutique/${shop.id}`, label: "Voir ma boutique", icon: BadgeCheck, testid: "profile-view-shop" },
    !shop && { to: "/vendre", label: "Ouvrir ma boutique", icon: Store, testid: "profile-open-shop", highlight: true },
    shop && { to: "/creer-produit", label: "Publier un produit", icon: Package, testid: "profile-new-product" },
    { to: "/messages", label: "Messages Forum CI", icon: MessageCircle, testid: "profile-messages" },
    { href: buildSupportMailto(), label: "Contactez-nous", icon: Mail, testid: "profile-contact-support", external: true },
    user.role === "admin" && { to: "/admin", label: "Panneau Admin", icon: Shield, testid: "profile-admin" },
  ].filter(Boolean);

  return (
    <div className="max-w-lg mx-auto px-3 sm:px-6 py-4">
      <div className="flex items-center gap-3 p-4 rounded-2xl bg-white border border-[#E8E6E1]">
        <div className="w-14 h-14 rounded-2xl bg-[#E65100] text-white flex items-center justify-center font-extrabold text-xl">
          {fullName[0]?.toUpperCase()}
        </div>
        <div className="min-w-0">
          <div className="font-bold text-[#1A1918] truncate">{fullName}</div>
          <div className="text-xs text-[#73706B]">{user.email || user.phone}</div>
          <div className="mt-1 inline-block text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded bg-[#F3F1EC] text-[#73706B]">
            {user.role === "admin" ? "Administrateur" : user.role === "merchant" ? "Marchand" : "Client"}
          </div>
        </div>
      </div>

      <div className="mt-4 space-y-2">
        {items.map((it) => {
          const Icon = it.icon;
          const inner = (
            <>
              <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${it.highlight ? "bg-[#E65100] text-white" : "bg-[#F3F1EC] text-[#1A1918]"}`}>
                <Icon className="h-5 w-5" />
              </div>
              <div className="font-semibold">{it.label}</div>
            </>
          );
          const cls = `flex items-center gap-3 p-4 rounded-xl border transition ${it.highlight ? "border-[#E65100] bg-[#FFF8F3] text-[#E65100]" : "border-[#E8E6E1] bg-white hover:border-[#E65100]"}`;
          if (it.external) {
            return (
              <a key={it.testid} href={it.href} data-testid={it.testid} className={cls}>{inner}</a>
            );
          }
          return (
            <Link key={it.to} to={it.to} data-testid={it.testid} className={cls}>{inner}</Link>
          );
        })}
      </div>

      <Button onClick={async () => { await logout(); nav("/"); }} data-testid="logout-btn" variant="outline" className="w-full h-12 rounded-xl mt-4 text-[#D32F2F] hover:text-[#D32F2F] hover:bg-red-50">
        <LogOut className="h-5 w-5 mr-1" /> Se déconnecter
      </Button>

      <div className="mt-6">
        <div className="flex items-center gap-2 mb-2">
          <Palette className="h-4 w-4 text-[#73706B]" />
          <Label className="text-xs font-bold uppercase tracking-wider text-[#73706B]">Apparence</Label>
        </div>
        <ThemeSwitcher />
      </div>
    </div>
  );
}

function Label({ children, className = "" }) {
  return <span className={className}>{children}</span>;
}
